import {css, StyleSheet} from 'aphrodite';
import React from 'react';

import {ZoomSlider} from 'molbio/maps/options/Sliders';
import {PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD, ZOOM_MAX, ZOOM_STEP} from 'search/blast/BLASTAlignmentUtil';
import {
  BlastAlignmentView,
  BlastAlignmentViewSwitchTrigger,
  trackBlastBarPairwiseViewSwitch,
} from 'search/blast/BLASTTracking';
import ViewMenu, {ViewMenuProps} from 'search/blast/ViewMenu';
import {Operator} from 'search/SearchConstants';
import {ButtonSize} from 'ui-library/buttons/Button';
import ToggleGroup from 'ui-library/ToggleGroup';

enum ZoomView {
  PAIRWISE = 'Pairwise',
  BAR = 'Bar',
}

export interface ZoomControlProps {
  zoomControl: {
    zoom: number;
    setZoom: (zoom: number) => void;
  };
  blastOperator?: Operator;
  viewMenu: ViewMenuProps;
}

export default function BlastViewControl({
  zoomControl,
  blastOperator,
  viewMenu,
}: ZoomControlProps): JSX.Element {
  const isBarView = zoomControl.zoom < PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD;
  return (
    <div className={css(styles.container)}>
      {blastOperator !== Operator.BLASTN && !isBarView && <ViewMenu {...viewMenu} />}

      <ToggleGroup<ZoomView>
        onChangeSelection={(selection: ZoomView) => {
          if (selection === ZoomView.BAR) {
            if (isBarView) {
              return;
            }
            trackBlastBarPairwiseViewSwitch(BlastAlignmentView.BAR, BlastAlignmentViewSwitchTrigger.CLICK);
            zoomControl.setZoom(0);
          } else {
            if (!isBarView) {
              return;
            }
            trackBlastBarPairwiseViewSwitch(
              BlastAlignmentView.PAIRWISE,
              BlastAlignmentViewSwitchTrigger.CLICK
            );
            zoomControl.setZoom(PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD);
          }
        }}
        selection={isBarView ? ZoomView.BAR : ZoomView.PAIRWISE}
        size={ButtonSize.SMALL}
        options={[
          {id: ZoomView.BAR, buttonProps: {text: 'Bar'}},
          {id: ZoomView.PAIRWISE, buttonProps: {text: 'Pairwise'}},
        ]}
      />

      <ZoomSlider
        min={0}
        max={ZOOM_MAX}
        value={zoomControl.zoom}
        onChange={(value: number) => {
          if (isBarView && value >= PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD) {
            trackBlastBarPairwiseViewSwitch(
              BlastAlignmentView.PAIRWISE,
              BlastAlignmentViewSwitchTrigger.ZOOM
            );
          } else if (!isBarView && value < PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD) {
            trackBlastBarPairwiseViewSwitch(BlastAlignmentView.BAR, BlastAlignmentViewSwitchTrigger.ZOOM);
          }
          zoomControl.setZoom(value);
        }}
        step={ZOOM_STEP}
        disabled={false}
        zoomSmaller={() => {
          if (zoomControl.zoom <= 0) {
            return;
          }
          const newZoom = zoomControl.zoom - ZOOM_STEP;
          if (!isBarView && newZoom < PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD) {
            trackBlastBarPairwiseViewSwitch(BlastAlignmentView.BAR, BlastAlignmentViewSwitchTrigger.ZOOM);
          }
          zoomControl.setZoom(newZoom);
        }}
        zoomBigger={() => {
          if (zoomControl.zoom >= ZOOM_MAX) {
            return;
          }
          const newZoom = zoomControl.zoom + ZOOM_STEP;
          if (isBarView && newZoom >= PAIRWISE_ALIGNMENT_ZOOM_THRESHOLD) {
            trackBlastBarPairwiseViewSwitch(
              BlastAlignmentView.PAIRWISE,
              BlastAlignmentViewSwitchTrigger.ZOOM
            );
          }
          zoomControl.setZoom(newZoom);
        }}
      />
    </div>
  );
}

const styles = StyleSheet.create({
  container: {
    display: 'flex',
    alignItems: 'center',
    gap: '20px',
  },
});
